import { useEffect, useState } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { logout } from '../api/client';
import { useAuthStore } from '../auth/store';
import { useNotifications } from '../api/queries/notifications';
import { GlobalSearch } from './GlobalSearch';
import { Sidebar } from './Sidebar';
import { SectionTabs } from './SectionTabs';
import { UserAvatar } from './UserAvatar';
import { BrandMark } from './BrandMark';
import './AppShell.css';

// The public marketing site is a separate host (packages/site). Overridable
// per environment; defaults to production apex.
const PUBLIC_SITE_URL = import.meta.env.VITE_PUBLIC_SITE_URL ?? 'https://stfors.com';

export function AppShell(): JSX.Element {
  const me = useAuthStore((s) => s.me);
  const setMe = useAuthStore((s) => s.setMe);
  const navigate = useNavigate();
  const location = useLocation();
  const mustChangePassword = me?.user.must_change_password === true;
  const [navOpen, setNavOpen] = useState(false);

  // Force the user to change their temp password before doing anything
  // else. The /settings page hosts the change-password form; we redirect
  // any other route there. /login can still be reached if the session
  // expires.
  useEffect(() => {
    if (!mustChangePassword) return;
    if (location.pathname === '/settings' || location.pathname === '/login') return;
    navigate('/settings', { replace: true });
  }, [mustChangePassword, location.pathname, navigate]);

  // Close the mobile nav drawer whenever the route changes.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  // Badge driver. Returns quickly since the unread query is cached + on
  // a 60s refetch interval; safe to call from the shell without
  // gating because /api/notifications is requireAuth-only.
  const notificationsQuery = useNotifications();
  const unreadCount = notificationsQuery.data?.unread_count ?? 0;

  const onLogout = async () => {
    await logout().catch(() => {});
    setMe(null);
    navigate('/login', { replace: true });
  };

  return (
    <div className={`app-shell${navOpen ? ' nav-open' : ''}`}>
      <aside className="app-sidebar">
        <div className="app-brand">
          <BrandMark />
        </div>
        <Sidebar />
      </aside>

      {/* Scrim closes the drawer on mobile when tapping outside it. */}
      <div className="app-scrim" onClick={() => setNavOpen(false)} aria-hidden="true" />

      <div className="app-body">
        <header className="app-topbar">
          <button
            type="button"
            className="app-nav-toggle"
            aria-label="Toggle navigation"
            aria-expanded={navOpen}
            onClick={() => setNavOpen((v) => !v)}
          >
            <Menu size={20} aria-hidden="true" />
          </button>
          <div className="app-topbar-spacer" />
          <div className="app-user">
            <GlobalSearch />
            <a
              href={PUBLIC_SITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="app-mini-link"
              title="Open the public marketing site"
            >
              Public
            </a>
            <NavLink
              to="/invitations"
              className={({ isActive }) => `app-mini-link ${isActive ? 'active' : ''}`}
              title="Invitations"
            >
              Invites
            </NavLink>
            <NavLink
              to="/notifications"
              className={({ isActive }) => `app-bell ${isActive ? 'active' : ''}`}
              title={unreadCount > 0 ? `${unreadCount} unread` : 'Notifications'}
              aria-label="Notifications"
            >
              🔔
              {unreadCount > 0 && (
                <span className="app-bell-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
              )}
            </NavLink>
            <NavLink
              to="/settings"
              end
              className={({ isActive }) => `app-gear ${isActive ? 'active' : ''}`}
              title="User Settings"
              aria-label="User Settings"
            >
              {/* Inline SVG keeps the icon crisp at any DPI and avoids the
                  emoji-vs-monochrome rendering inconsistency of the gear
                  glyph (⚙ vs ⚙️) across platforms. */}
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
            </NavLink>
            <NavLink
              to="/settings"
              className={({ isActive }) => `app-user-name ${isActive ? 'active' : ''}`}
            >
              <UserAvatar
                userId={me?.user.id}
                hasAvatar={me?.user.has_avatar ?? !!me?.user.avatar_path}
                refreshKey={me?.user.avatar_updated_at ?? me?.user.avatar_path ?? ''}
                className="app-user-avatar"
              />
              {me?.user.display_name}
            </NavLink>
            <button onClick={onLogout} className="btn-charcoal-secondary">Sign out</button>
          </div>
        </header>

        {mustChangePassword && (
          <div className="app-must-change-banner" role="alert">
            You must change your password before you can use the app.
            {location.pathname !== '/settings' && (
              <>
                {' '}
                <NavLink to="/settings">Go to User Settings →</NavLink>
              </>
            )}
          </div>
        )}

        <SectionTabs />

        <main className="app-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
